/**
 * Wire format ⇄ domain model translation.
 *
 * The PHP Site API speaks snake_case JSON matching `internal/api-contract.md`.
 * Everything the UI sees is the camelCase domain model from `types.ts`. This
 * file is the *only* place that knows both.
 *
 * Normalizers are deliberately tolerant: a missing optional field yields a
 * sensible empty value instead of throwing, because a half-rendered profile is
 * better than a blank error page.
 */

import { canonicalCountryCode } from '../lib/format';
import { parseSocialLinks } from '../lib/socialLinks';
import type {
  Achievement,
  AchievementKind,
  ApiMeta,
  AppNotification,
  AuthSession,
  BanState,
  CommunityBoard,
  CommunityComment,
  CommunityPost,
  Country,
  DeviceBinding,
  BoundDevice,
  GamePlatform,
  GameRef,
  GameToken,
  GameVersion,
  LeaderboardPage,
  LocalizedText,
  MyPoints,
  MyPointsRank,
  MySpeedrunRank,
  NamedRef,
  NotificationType,
  Paginated,
  PlayerPoints,
  PlayerProfile,
  PlayerRole,
  PlayerSummary,
  PointsEntry,
  PointsLeaderboardRow,
  PointsSource,
  PostStatus,
  SiteDictionaries,
  SiteNavLink,
  SiteSettings,
  SpeedrunCategory,
  SpeedrunLeaderboardRow,
  SpeedrunPlayer,
  SpeedrunStatus,
  SpeedrunSubmission,
  SpeedrunTier,
  Viewer,
} from './types';

/* --------------------------------------------------------------- primitives */

export interface WireLocalized {
  zh?: string | null;
  en?: string | null;
}

export interface WirePaginated<T> {
  items?: T[] | null;
}

export interface WireMeta {
  page?: number;
  per_page?: number;
  total?: number;
  total_pages?: number;
  has_more?: boolean;
  self?: unknown;
  hidden_locked_count?: number;
  unlock_count?: number;
  recent_unlockers?: unknown;
  notice?: string;
  [key: string]: unknown;
}

const str = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : typeof value === 'number' ? String(value) : fallback;

const num = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value)
    ? value
    : typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))
      ? Number(value)
      : fallback;

const bool = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean'
    ? value
    : value === 1 || value === '1'
      ? true
      : value === 0 || value === '0'
        ? false
        : fallback;

const nullableStr = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

function genderValue(value: unknown): PlayerProfile['gender'] {
  return value === 'male' || value === 'female' || value === 'other' || value === 'prefer_not_to_say'
    ? value
    : null;
}

const nullableNum = (value: unknown): number | null =>
  value === null || value === undefined || value === '' ? null : num(value);

const intId = (value: unknown, fallback = 0): number => Math.trunc(num(value, fallback));

export function localized(value: WireLocalized | string | null | undefined): LocalizedText {
  if (typeof value === 'string') return { zh: value, en: value };
  const zh = str(value?.zh);
  const en = str(value?.en);
  return { zh: zh || en, en: en || zh };
}

export function pickLocalized(
  pair: WireLocalized | string | null | undefined,
  localizedField: unknown,
): { pair: LocalizedText; localized: string } {
  const pairValue = localized(pair);
  const resolved = typeof localizedField === 'string' && localizedField.length > 0 ? localizedField : pairValue.zh;
  return { pair: pairValue, localized: resolved };
}

export function apiMeta(raw: WireMeta | null | undefined): ApiMeta {
  return {
    page: raw?.page,
    perPage: raw?.per_page,
    total: raw?.total,
    totalPages: raw?.total_pages,
    hasMore: raw?.has_more,
    self: raw?.self,
    hiddenLockedCount: raw?.hidden_locked_count,
    unlockCount: raw?.unlock_count,
    notice: typeof raw?.notice === 'string' ? raw.notice : undefined,
    ...raw,
  };
}

export function paginated<W, T>(
  payload: WirePaginated<W> | null | undefined,
  meta: WireMeta | ApiMeta | null | undefined,
  map: (raw: W) => T,
): Paginated<T> {
  const items = (payload?.items ?? []).map(map);
  const page = num(meta && 'page' in meta ? meta.page : undefined, 1);
  const perPage = num(
    meta && ('perPage' in meta ? meta.perPage : (meta as WireMeta).per_page),
    items.length || 20,
  );
  const total = num(meta && 'total' in meta ? meta.total : undefined, items.length);
  const totalPages = num(
    meta && ('totalPages' in meta ? meta.totalPages : (meta as WireMeta).total_pages),
    perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1,
  );
  const hasMore =
    typeof (meta as WireMeta | undefined)?.has_more === 'boolean'
      ? Boolean((meta as WireMeta).has_more)
      : typeof (meta as ApiMeta | undefined)?.hasMore === 'boolean'
        ? Boolean((meta as ApiMeta).hasMore)
        : page < totalPages;
  return { items, page, perPage, total, totalPages, hasMore };
}

/* ------------------------------------------------------------------ players */

export interface WireRole {
  code?: string;
  name?: WireLocalized;
  badge_color?: string;
}

export interface WirePlayerSummary {
  public_player_id?: string;
  nickname?: string;
  avatar_url?: string | null;
  country_code?: string | null;
  roles?: WireRole[] | null;
  points?: number;
  achievement_count?: number;
  speedrun_count?: number;
  followers_count?: number;
  following_count?: number;
  is_following?: boolean | null;
  bio?: string | null;
  social_links?: string[] | null;
  birthday?: string | null;
  gender?: string | null;
  joined_at?: string;
}

export interface WirePlayerSelf extends WirePlayerSummary {
  email?: string | null;
  username?: string;
  locale?: string;
  status?: string;
  email_verified?: boolean;
  profile_incomplete?: boolean;
  device_bound?: boolean;
  unread_notifications?: number;
  permissions?: string[] | null;
  is_super_admin?: boolean;
  ban?: WireBan | null;
}

export interface WireBan {
  type?: string;
  scope?: string;
  reason?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  ends_at_unix?: number | null;
  is_permanent?: boolean;
}

export function playerRole(raw: WireRole | null | undefined): PlayerRole {
  return {
    code: str(raw?.code),
    name: localized(raw?.name),
    badgeColor: str(raw?.badge_color, '#64748b'),
  };
}

export function playerSummary(raw: WirePlayerSummary | null | undefined): PlayerSummary {
  return {
    publicPlayerId: str(raw?.public_player_id),
    nickname: str(raw?.nickname, 'Player'),
    avatarUrl: nullableStr(raw?.avatar_url),
    countryCode: canonicalCountryCode(nullableStr(raw?.country_code)),
    roles: (raw?.roles ?? []).map(playerRole),
  };
}

export function playerProfile(raw: WirePlayerSummary | null | undefined): PlayerProfile {
  return {
    ...playerSummary(raw),
    bio: nullableStr(raw?.bio),
    socialLinks: parseSocialLinks(raw?.social_links),
    birthday: nullableStr(raw?.birthday),
    gender: genderValue(raw?.gender),
    joinedAt: str(raw?.joined_at),
    points: num(raw?.points),
    achievementCount: num(raw?.achievement_count),
    speedrunCount: num(raw?.speedrun_count),
    followersCount: num(raw?.followers_count),
    followingCount: num(raw?.following_count),
    isFollowing: raw?.is_following == null ? null : bool(raw.is_following),
  };
}

export function banState(raw: WireBan | null | undefined): BanState | null {
  if (!raw) return null;
  const type = str(raw.type, 'temporary') === 'permanent' ? 'permanent' : 'temporary';
  return {
    type,
    scope: str(raw.scope, 'account') === 'community' ? 'community' : 'account',
    reason: nullableStr(raw.reason),
    startsAt: nullableStr(raw.starts_at),
    endsAt: nullableStr(raw.ends_at),
    isPermanent: bool(raw.is_permanent, type === 'permanent'),
  };
}

export function viewer(raw: WirePlayerSelf | null | undefined): Viewer {
  return {
    ...playerProfile(raw),
    email: nullableStr(raw?.email),
    username: str(raw?.username),
    locale: raw?.locale === 'en' ? 'en' : 'zh',
    status: str(raw?.status, 'active'),
    emailVerified: bool(raw?.email_verified),
    birthday: nullableStr(raw?.birthday),
    gender: genderValue(raw?.gender),
    profileIncomplete: bool(raw?.profile_incomplete),
    deviceBound: bool(raw?.device_bound),
    unreadNotifications: num(raw?.unread_notifications),
    permissions: raw?.permissions ?? [],
    isSuperAdmin: bool(raw?.is_super_admin),
    ban: banState(raw?.ban ?? null),
  };
}

/* ------------------------------------------------------------- achievements */

export interface WireNamedRef {
  id?: number;
  code?: string;
  name?: WireLocalized;
  name_localized?: string;
}

export interface WireAchievement {
  id?: number;
  code?: string;
  game?: WireNamedRef;
  type?: WireNamedRef;
  name?: WireLocalized;
  description?: WireLocalized;
  name_localized?: string;
  description_localized?: string;
  icon_url?: string | null;
  kind?: string;
  is_hidden?: boolean;
  points?: number;
  unlocked?: boolean;
  unlocked_at?: string | null;
}

function namedRef(raw: WireNamedRef | null | undefined, fallbackCode = 'unknown'): NamedRef {
  const { pair } = pickLocalized(raw?.name, raw?.name_localized);
  return { id: intId(raw?.id), code: str(raw?.code, fallbackCode), name: pair };
}

export function achievement(raw: WireAchievement | null | undefined): Achievement {
  const kind = str(raw?.kind, 'normal') as AchievementKind;
  const name = pickLocalized(raw?.name, raw?.name_localized);
  const description = pickLocalized(raw?.description, raw?.description_localized);
  return {
    id: intId(raw?.id),
    code: str(raw?.code),
    game: namedRef(raw?.game, 'medaka'),
    type: namedRef(raw?.type, 'story'),
    name: name.pair,
    description: description.pair,
    nameLocalized: name.localized,
    descriptionLocalized: description.localized,
    iconUrl: nullableStr(raw?.icon_url),
    kind: kind === 'special' ? 'special' : 'normal',
    isHidden: bool(raw?.is_hidden),
    points: num(raw?.points),
    unlocked: bool(raw?.unlocked),
    unlockedAt: nullableStr(raw?.unlocked_at),
  };
}

/* ---------------------------------------------------------------- speedruns */

export interface WirePlatform {
  id?: number;
  code?: string;
  name?: WireLocalized | string;
  name_localized?: string;
}

export interface WireVersion {
  id?: number;
  game_id?: number;
  code?: string;
  name?: string;
  released_at?: string | null;
}

export interface WireTier {
  id?: number;
  name?: WireLocalized;
  name_localized?: string;
  max_time_ms?: number;
  points?: number;
}

export interface WireCategory {
  id?: number;
  game_id?: number;
  code?: string;
  name?: WireLocalized;
  name_localized?: string;
  description?: WireLocalized;
  description_localized?: string;
  timing_method?: string;
  tiers?: WireTier[] | null;
}

export interface WireGame {
  id?: number;
  code?: string;
  name?: WireLocalized;
  name_localized?: string;
  is_enabled?: boolean | number | string;
  versions?: WireVersion[] | null;
  categories?: WireCategory[] | null;
}

export interface WireRun {
  id?: number;
  player?: {
    public_player_id?: string;
    nickname?: string;
    avatar_url?: string | null;
    country_code?: string | null;
  };
  game?: WireGame;
  platform?: WirePlatform;
  version?: WireVersion;
  category?: WireCategory;
  time_ms?: number;
  time_formatted?: string;
  played_at?: string | null;
  status?: string;
  tier?: WireTier | null;
  points_awarded?: number;
  notes?: string | null;
  proof_url?: string | null;
  review?: { note?: string | null; reviewed_at?: string | null } | null;
  created_at?: string;
}

export function formatTimeMs(timeMs: number): string {
  const total = Math.max(0, Math.round(timeMs));
  const hours = Math.floor(total / 3_600_000);
  const minutes = Math.floor((total % 3_600_000) / 60_000);
  const seconds = Math.floor((total % 60_000) / 1000);
  const millis = total % 1000;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

export function gameRef(raw: WireGame | null | undefined): GameRef {
  const { pair } = pickLocalized(raw?.name, raw?.name_localized);
  return {
    id: intId(raw?.id, 1),
    code: str(raw?.code, 'medaka'),
    name: pair,
    isEnabled: raw?.is_enabled === undefined || raw?.is_enabled === null ? true : bool(raw.is_enabled, true),
  };
}

export function uniqueById<T extends { id: number }>(items: T[]): T[] {
  const seen = new Set<number>();
  const out: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

export function platform(raw: WirePlatform | null | undefined): GamePlatform {
  const name = pickLocalized(typeof raw?.name === 'string' ? { en: raw.name, zh: raw.name } : raw?.name, raw?.name_localized);
  return {
    id: intId(raw?.id),
    code: str(raw?.code, str(typeof raw?.name === 'string' ? raw.name : '')),
    name: name.pair,
    nameLocalized: name.localized,
  };
}

export function version(raw: WireVersion | null | undefined): GameVersion {
  return {
    id: intId(raw?.id),
    gameId: intId(raw?.game_id, 1),
    code: str(raw?.code, str(raw?.name)),
    name: str(raw?.name),
    releasedAt: nullableStr(raw?.released_at),
  };
}

export function tier(raw: WireTier | null | undefined): SpeedrunTier {
  const name = pickLocalized(raw?.name, raw?.name_localized);
  return {
    id: intId(raw?.id),
    name: name.pair,
    nameLocalized: name.localized,
    maxTimeMs: num(raw?.max_time_ms),
    points: num(raw?.points),
  };
}

export function category(raw: WireCategory | null | undefined): SpeedrunCategory {
  const name = pickLocalized(raw?.name, raw?.name_localized);
  const description = pickLocalized(raw?.description, raw?.description_localized);
  return {
    id: intId(raw?.id),
    gameId: intId(raw?.game_id, 1),
    code: str(raw?.code),
    name: name.pair,
    nameLocalized: name.localized,
    description: description.pair,
    timingMethod: raw?.timing_method === 'igt' ? 'igt' : 'rta',
    tiers: (raw?.tiers ?? []).map(tier).sort((a, b) => a.maxTimeMs - b.maxTimeMs),
  };
}

const RUN_STATUSES: SpeedrunStatus[] = ['pending', 'approved', 'rejected', 'revoked', 'withdrawn'];

export function speedrunPlayer(raw: WireRun['player']): SpeedrunPlayer {
  return {
    publicPlayerId: str(raw?.public_player_id),
    nickname: str(raw?.nickname, 'Player'),
    avatarUrl: nullableStr(raw?.avatar_url),
    countryCode: canonicalCountryCode(nullableStr(raw?.country_code)),
  };
}

export function run(raw: WireRun | null | undefined): SpeedrunSubmission {
  const status = str(raw?.status, 'pending') as SpeedrunStatus;
  const timeMs = num(raw?.time_ms);
  return {
    id: intId(raw?.id),
    player: speedrunPlayer(raw?.player),
    game: gameRef(raw?.game),
    platform: platform(raw?.platform),
    version: version(raw?.version),
    category: category(raw?.category),
    timeMs,
    timeFormatted: str(raw?.time_formatted, formatTimeMs(timeMs)),
    playedAt: nullableStr(raw?.played_at),
    status: RUN_STATUSES.includes(status) ? status : 'pending',
    tier: raw?.tier ? tier(raw.tier) : null,
    pointsAwarded: num(raw?.points_awarded),
    notes: nullableStr(raw?.notes),
    proofUrl: nullableStr(raw?.proof_url),
    review: raw?.review
      ? { note: nullableStr(raw.review.note), reviewedAt: nullableStr(raw.review.reviewed_at) }
      : null,
    createdAt: str(raw?.created_at),
  };
}

/* ------------------------------------------------------------------- points */

export interface WirePointsEntry {
  id?: number;
  delta?: number;
  balance_after?: number;
  source_type?: string;
  source_id?: number | null;
  reason?: string;
  reverses_entry_id?: number | null;
  created_at?: string;
  created_at_unix?: number | null;
}

export interface WirePointsSummary {
  points?: number;
  rank?: number | null;
  total_earned?: number;
  total_deducted?: number;
  breakdown?: {
    achievement?: number;
    speedrun?: number;
    admin_adjust?: number;
    other?: number;
  } | null;
}

const POINTS_SOURCES: PointsSource[] = ['achievement', 'speedrun', 'admin_adjust', 'reversal', 'system'];

const pointsSource = (value: unknown): PointsSource => {
  const source = str(value, 'system') as PointsSource;
  return POINTS_SOURCES.includes(source) ? source : 'system';
};

export function pointsEntry(raw: WirePointsEntry): PointsEntry {
  return {
    id: intId(raw?.id),
    delta: num(raw?.delta),
    balanceAfter: num(raw?.balance_after),
    sourceType: pointsSource(raw?.source_type),
    sourceId: raw?.source_id == null ? null : intId(raw.source_id),
    reason: str(raw?.reason),
    reversesEntryId: raw?.reverses_entry_id == null ? null : intId(raw.reverses_entry_id),
    createdAt: str(raw?.created_at),
    createdAtUnix: nullableNum(raw?.created_at_unix),
  };
}

export function myPoints(raw: WirePointsSummary | null | undefined): MyPoints {
  return {
    points: num(raw?.points),
    rank: nullableNum(raw?.rank),
    totalEarned: num(raw?.total_earned),
    totalDeducted: num(raw?.total_deducted),
    breakdown: {
      achievement: num(raw?.breakdown?.achievement),
      speedrun: num(raw?.breakdown?.speedrun),
      adminAdjust: num(raw?.breakdown?.admin_adjust),
      other: num(raw?.breakdown?.other),
    },
  };
}

export function playerPoints(raw: WirePointsSummary | null | undefined): PlayerPoints {
  const summary = myPoints(raw);
  return {
    points: summary.points,
    rank: summary.rank,
    breakdown: summary.breakdown,
  };
}

/* -------------------------------------------------------------- leaderboard */

export interface WirePointsRow {
  rank?: number;
  player?: WirePlayerSummary;
  public_player_id?: string;
  nickname?: string;
  avatar_url?: string | null;
  country_code?: string | null;
  points?: number;
  achievement_count?: number;
}

export interface WireSpeedrunRow {
  rank?: number;
  player?: WirePlayerSummary;
  time_ms?: number;
  time_formatted?: string;
  platform?: WirePlatform;
  version?: WireVersion;
  tier?: WireTier | null;
  submission_id?: number;
  played_at?: string | null;
}

function rowPlayer(raw: WirePointsRow | WireSpeedrunRow): PlayerSummary {
  if (raw.player) return playerSummary(raw.player);
  const flat = raw as WirePointsRow;
  return playerSummary({
    public_player_id: flat.public_player_id,
    nickname: flat.nickname,
    avatar_url: flat.avatar_url,
    country_code: flat.country_code,
  });
}

export function pointsRow(raw: WirePointsRow): PointsLeaderboardRow {
  return {
    rank: num(raw?.rank),
    player: rowPlayer(raw),
    points: num(raw?.points),
    achievementCount: num(raw?.achievement_count),
  };
}

export function speedrunRow(raw: WireSpeedrunRow): SpeedrunLeaderboardRow {
  const timeMs = num(raw?.time_ms);
  return {
    rank: num(raw?.rank),
    player: rowPlayer(raw),
    timeMs,
    timeFormatted: str(raw?.time_formatted, formatTimeMs(timeMs)),
    platform: platform(raw?.platform),
    version: version(raw?.version),
    tier: raw?.tier ? tier(raw.tier) : null,
    submissionId: intId(raw?.submission_id),
    playedAt: nullableStr(raw?.played_at),
  };
}

export function leaderboardPage<W, T>(
  payload: WirePaginated<W> | null | undefined,
  meta: WireMeta | null | undefined,
  map: (raw: W) => T,
): LeaderboardPage<T> {
  return {
    ...paginated(payload, meta, map),
    self: meta?.self ? map(meta.self as W) : null,
  };
}

export function myPointsRank(raw: Record<string, unknown> | null | undefined): MyPointsRank {
  return {
    rank: nullableNum(raw?.rank),
    totalRanked: num(raw?.total_ranked),
    points: num(raw?.points),
  };
}

export function mySpeedrunRank(raw: Record<string, unknown> | null | undefined): MySpeedrunRank {
  return {
    rank: nullableNum(raw?.rank),
    totalRanked: num(raw?.total_ranked),
    timeMs: nullableNum(raw?.time_ms),
    timeFormatted: nullableStr(raw?.time_formatted),
    submissionId: raw?.submission_id == null ? null : intId(raw.submission_id),
  };
}

/* ---------------------------------------------------------------- community */

export interface WireBoard {
  id?: number;
  code?: string;
  name?: WireLocalized;
  name_localized?: string;
  description?: WireLocalized;
  description_localized?: string;
}

export interface WirePost {
  id?: number;
  board_id?: number;
  board?: WireBoard | null;
  user?: WirePlayerSummary;
  author?: WirePlayerSummary;
  title?: string;
  body?: string;
  status?: string;
  like_count?: number;
  comment_count?: number;
  view_count?: number;
  is_liked_by_me?: boolean;
  is_pinned?: boolean;
  is_locked?: boolean;
  created_at?: string;
  updated_at?: string | null;
}

export interface WireComment {
  id?: number;
  post_id?: number;
  parent_id?: number | null;
  parent_author?: WirePlayerSummary | null;
  user?: WirePlayerSummary;
  author?: WirePlayerSummary;
  body?: string;
  status?: string;
  like_count?: number;
  is_liked_by_me?: boolean;
  created_at?: string;
  replies?: WireComment[] | null;
  children?: WireComment[] | null;
}

export function board(raw: WireBoard | null | undefined): CommunityBoard {
  const name = pickLocalized(raw?.name, raw?.name_localized);
  const description = pickLocalized(raw?.description, raw?.description_localized);
  return {
    id: intId(raw?.id),
    code: str(raw?.code),
    name: name.pair,
    nameLocalized: name.localized,
    description: description.pair,
  };
}

const POST_STATUSES: PostStatus[] = ['published', 'pending', 'hidden', 'removed'];

export function post(raw: WirePost | null | undefined): CommunityPost {
  const status = str(raw?.status, 'published') as PostStatus;
  return {
    id: intId(raw?.id),
    boardId: intId(raw?.board_id ?? raw?.board?.id),
    board: raw?.board ? board(raw.board) : null,
    author: playerSummary(raw?.author ?? raw?.user),
    title: str(raw?.title),
    body: str(raw?.body),
    status: POST_STATUSES.includes(status) ? status : 'published',
    likeCount: num(raw?.like_count),
    commentCount: num(raw?.comment_count),
    viewCount: num(raw?.view_count),
    isLikedByMe: bool(raw?.is_liked_by_me),
    isPinned: bool(raw?.is_pinned),
    isLocked: bool(raw?.is_locked),
    createdAt: str(raw?.created_at),
    updatedAt: nullableStr(raw?.updated_at),
  };
}

export function comment(raw: WireComment): CommunityComment {
  const nested = raw.replies ?? raw.children ?? [];
  return {
    id: intId(raw?.id),
    postId: intId(raw?.post_id),
    parentId: raw?.parent_id == null ? null : intId(raw.parent_id),
    parentAuthor: raw?.parent_author ? playerSummary(raw.parent_author) : null,
    author: playerSummary(raw?.author ?? raw?.user),
    body: str(raw?.body),
    status: str(raw?.status, 'published'),
    likeCount: num(raw?.like_count),
    isLikedByMe: bool(raw?.is_liked_by_me),
    createdAt: str(raw?.created_at),
    replies: nested.map(comment),
  };
}

/* ------------------------------------------------------------ notifications */

export interface WireNotification {
  id?: number;
  type?: string;
  title?: string;
  body?: string;
  data?: Record<string, unknown> | null;
  link?: string | null;
  read?: boolean;
  created_at?: string;
}

const NOTIFICATION_TYPES: NotificationType[] = [
  'achievement_unlocked',
  'achievement_revoked',
  'speedrun_approved',
  'speedrun_rejected',
  'speedrun_revoked',
  'points_adjusted',
  'account_banned',
  'account_unbanned',
  'account_updated',
  'post_commented',
  'post_liked',
  'comment_replied',
  'new_follower',
  'report_resolved',
  'admin_message',
  'system',
];

export function notification(raw: WireNotification): AppNotification {
  const type = str(raw?.type, 'system') as NotificationType;
  return {
    id: intId(raw?.id),
    type: NOTIFICATION_TYPES.includes(type) ? type : 'system',
    title: str(raw?.title),
    body: str(raw?.body),
    data: raw?.data ?? {},
    link: nullableStr(raw?.link),
    read: bool(raw?.read),
    createdAt: str(raw?.created_at),
  };
}

/* --------------------------------------------------------------- site state */

export interface WireSiteConfig {
  site_name?: string;
  site_description?: string;
  logo_url?: string | null;
  favicon_url?: string | null;
  default_locale?: string;
  available_locales?: string[] | null;
  announcement?: {
    enabled?: boolean;
    text?: string;
    text_zh?: string;
    text_en?: string;
  } | null;
  features?: {
    web_registration?: boolean;
    game_registration?: boolean;
    avatar_upload?: boolean;
    community?: boolean;
    speedrun_submission?: boolean;
  } | null;
  maintenance?: {
    enabled?: boolean;
    message?: string | null;
    message_zh?: string | null;
    message_en?: string | null;
  } | null;
  developer_contact?: {
    intro?: { zh?: string; en?: string } | null;
    intro_localized?: string;
    fields?: Array<{
      label?: { zh?: string; en?: string } | null;
      label_localized?: string;
      value?: string;
    }> | null;
  } | null;
  terms?: { zh?: string | null; en?: string | null; localized?: string | null } | null;
  legal?: {
    column_title?: { zh?: string; en?: string } | null;
    community_guidelines?: Array<{ zh?: string; en?: string; localized?: string } | null> | null;
    documents?: Array<{
      id?: number;
      code?: string;
      name?: { zh?: string; en?: string } | null;
      name_localized?: string;
      body?: { zh?: string; en?: string } | null;
      body_localized?: string;
      sort_order?: number;
    }> | null;
  } | null;
  nav_links?: {
    header?: Array<{
      id?: number;
      code?: string;
      url?: string;
      name?: { zh?: string; en?: string } | null;
      name_localized?: string;
      sort_order?: number;
    }> | null;
    footer?: Array<{
      id?: number;
      code?: string;
      url?: string;
      name?: { zh?: string; en?: string } | null;
      name_localized?: string;
      sort_order?: number;
    }> | null;
  } | null;
  community_rules_url?: string | null;
  privacy_policy_url?: string | null;
  server_time?: string;
  api_version?: string;
}

export function siteSettings(raw: WireSiteConfig | null | undefined): SiteSettings {
  const features = raw?.features ?? {};
  const locales = (raw?.available_locales ?? ['zh', 'en']).filter(
    (value): value is 'zh' | 'en' => value === 'zh' || value === 'en',
  );
  const contact = raw?.developer_contact;
  const introZh = str(contact?.intro?.zh);
  const introEn = str(contact?.intro?.en);
  return {
    siteName: str(raw?.site_name, 'Medaka Games Reward'),
    siteDescription: str(raw?.site_description),
    logoUrl: nullableStr(raw?.logo_url),
    faviconUrl: nullableStr(raw?.favicon_url),
    defaultLocale: raw?.default_locale === 'en' ? 'en' : 'zh',
    availableLocales: locales.length > 0 ? locales : ['zh', 'en'],
    announcement: raw?.announcement
      ? {
          enabled: bool(raw.announcement.enabled),
          text: str(raw.announcement.text),
          textZh: str(raw.announcement.text_zh, str(raw.announcement.text)),
          textEn: str(raw.announcement.text_en, str(raw.announcement.text)),
        }
      : null,
    features: {
      webRegistration: bool(features.web_registration, true),
      gameRegistration: bool(features.game_registration, true),
      avatarUpload: bool(features.avatar_upload, true),
      community: bool(features.community, true),
      speedrunSubmission: bool(features.speedrun_submission, true),
    },
    maintenance: {
      enabled: bool(raw?.maintenance?.enabled),
      message: nullableStr(raw?.maintenance?.message),
      messageZh: str(raw?.maintenance?.message_zh, str(raw?.maintenance?.message)),
      messageEn: str(raw?.maintenance?.message_en, str(raw?.maintenance?.message)),
    },
    developerContact: {
      intro: { zh: introZh, en: introEn },
      introLocalized: str(contact?.intro_localized, introZh || introEn),
      fields: (contact?.fields ?? [])
        .filter((field) => str(field?.value) !== '')
        .map((field) => ({
          label: { zh: str(field?.label?.zh), en: str(field?.label?.en) },
          labelLocalized: str(field?.label_localized, str(field?.label?.zh) || str(field?.label?.en)),
          value: str(field?.value),
        })),
    },
    terms: {
      zh: str(raw?.terms?.zh),
      en: str(raw?.terms?.en),
      localized: str(raw?.terms?.localized, str(raw?.terms?.zh) || str(raw?.terms?.en)),
    },
    legal: mapLegal(raw?.legal, raw?.terms),
    communityRulesUrl: str(raw?.community_rules_url),
    privacyPolicyUrl: str(raw?.privacy_policy_url),
    navLinks: {
      header: mapNavLinks(raw?.nav_links?.header),
      footer: mapNavLinks(raw?.nav_links?.footer),
    },
    serverTime: nullableStr(raw?.server_time),
    apiVersion: str(raw?.api_version, 'v1'),
  };
}

function mapLegal(
  raw: WireSiteConfig['legal'] | null | undefined,
  terms: WireSiteConfig['terms'] | null | undefined,
): SiteSettings['legal'] {
  const titleZh = str(raw?.column_title?.zh, '社区规则及条款');
  const titleEn = str(raw?.column_title?.en, 'Community rules and terms');
  const blocks = (raw?.community_guidelines ?? []).map((block) => ({
    zh: str(block?.zh),
    en: str(block?.en, str(block?.localized)),
  }));
  while (blocks.length < 2) blocks.push({ zh: '', en: '' });
  const documents = (raw?.documents ?? [])
    .filter((row) => str(row?.code) !== '')
    .map((row) => {
      const nameZh = str(row?.name?.zh);
      const nameEn = str(row?.name?.en);
      const bodyZh = str(row?.body?.zh);
      const bodyEn = str(row?.body?.en);
      return {
        id: intId(row?.id),
        code: str(row?.code),
        name: { zh: nameZh || nameEn, en: nameEn || nameZh },
        nameLocalized: str(row?.name_localized, nameZh || nameEn),
        body: { zh: bodyZh, en: bodyEn },
        bodyLocalized: str(row?.body_localized, bodyZh || bodyEn),
      };
    });
  if (documents.length === 0 && (str(terms?.zh) !== '' || str(terms?.en) !== '')) {
    documents.push({
      id: 0,
      code: 'terms',
      name: { zh: '条款', en: 'Terms' },
      nameLocalized: '条款',
      body: { zh: str(terms?.zh), en: str(terms?.en) },
      bodyLocalized: str(terms?.localized, str(terms?.zh) || str(terms?.en)),
    });
  }
  return {
    columnTitle: { zh: titleZh, en: titleEn },
    communityGuidelines: blocks.slice(0, 2),
    documents,
  };
}

function mapNavLinks(
  rows: NonNullable<WireSiteConfig['nav_links']>['header'] | null | undefined,
): SiteNavLink[] {
  return (rows ?? [])
    .filter((row) => str(row?.url) !== '')
    .map((row) => {
      const zh = str(row?.name?.zh);
      const en = str(row?.name?.en);
      return {
        id: intId(row?.id),
        code: str(row?.code),
        url: str(row?.url),
        name: { zh, en },
        nameLocalized: str(row?.name_localized, zh || en),
      };
    });
}

export interface WireCountry {
  code?: string;
  name_zh?: string;
  name_en?: string;
}

export function country(raw: WireCountry): Country {
  return { code: str(raw?.code), nameZh: str(raw?.name_zh), nameEn: str(raw?.name_en) };
}

export interface WireDictionaries {
  games?: WireGame[] | null;
  platforms?: WirePlatform[] | null;
  versions?: WireVersion[] | null;
  categories?: WireCategory[] | null;
  tiers?: WireTier[] | null;
  achievement_types?: WireNamedRef[] | null;
  boards?: WireBoard[] | null;
}

export function dictionaries(raw: WireDictionaries | null | undefined): SiteDictionaries {
  const tiersByCategory = new Map<number, WireTier[]>();
  for (const item of raw?.tiers ?? []) {
    const categoryId = intId((item as WireTier & { category_id?: number }).category_id);
    const list = tiersByCategory.get(categoryId) ?? [];
    list.push(item);
    tiersByCategory.set(categoryId, list);
  }
  const categories = (raw?.categories ?? []).map((item) => {
    const mapped = category(item);
    if (mapped.tiers.length === 0) {
      mapped.tiers = (tiersByCategory.get(mapped.id) ?? []).map(tier).sort((a, b) => a.maxTimeMs - b.maxTimeMs);
    }
    return mapped;
  });
  return {
    games: (raw?.games ?? []).map(gameRef),
    platforms: (raw?.platforms ?? []).map(platform),
    versions: (raw?.versions ?? []).map(version),
    categories,
    achievementTypes: (raw?.achievement_types ?? []).map((item) => namedRef(item)),
    boards: (raw?.boards ?? []).map(board),
  };
}

export interface WireSession {
  id?: string | number;
  ip_masked?: string;
  device_hint?: string;
  last_active_at?: string;
  current?: boolean;
}

export function authSession(raw: WireSession): AuthSession {
  return {
    id: str(raw?.id),
    ipMasked: str(raw?.ip_masked),
    deviceHint: str(raw?.device_hint),
    lastActiveAt: str(raw?.last_active_at),
    current: bool(raw?.current),
  };
}

export interface WireGameToken {
  id?: number;
  device_hint?: string;
  created_at?: string;
  last_used_at?: string | null;
  expires_at?: string | null;
}

export function gameToken(raw: WireGameToken): GameToken {
  return {
    id: intId(raw?.id),
    deviceHint: str(raw?.device_hint),
    createdAt: str(raw?.created_at),
    lastUsedAt: nullableStr(raw?.last_used_at),
    expiresAt: nullableStr(raw?.expires_at),
  };
}

export interface WireDeviceSlot {
  platform?: string;
  code?: string;
  name_zh?: string;
  name_en?: string;
  bound?: boolean;
  device?: {
    platform?: string;
    model?: string;
    uid_masked?: string;
    bound_at?: string;
    status?: string;
  } | null;
}

export interface WireDevice {
  bound?: boolean;
  device?: WireDeviceSlot['device'];
  slots?: WireDeviceSlot[] | null;
}

function boundDeviceFrom(
  device: NonNullable<WireDeviceSlot['device']>,
  slot?: WireDeviceSlot,
): BoundDevice {
  const code = str(slot?.code || slot?.platform || device.platform);
  return {
    platform: code,
    nameZh: str(slot?.name_zh, code),
    nameEn: str(slot?.name_en, code),
    model: str(device.model),
    uidMasked: str(device.uid_masked),
    boundAt: str(device.bound_at),
    status: str(device.status),
  };
}

export function deviceBinding(raw: WireDevice | null | undefined): DeviceBinding {
  const fromSlots = (raw?.slots ?? [])
    .filter((slot): slot is WireDeviceSlot & { device: NonNullable<WireDeviceSlot['device']> } =>
      Boolean(slot?.bound && slot.device),
    )
    .map((slot) => boundDeviceFrom(slot.device, slot));
  const fallback = raw?.device ? boundDeviceFrom(raw.device) : null;
  const devices = fromSlots.length > 0 ? fromSlots : fallback ? [fallback] : [];
  return {
    bound: devices.length > 0,
    device: devices[0] ?? null,
    devices,
  };
}
